import React, { FC, useEffect, useState } from "react"
import { SortParams, SummaryTrackRoyalty } from "../../../../types"
import { KSpan, KTooltip } from "kahuna-base-react-components"
import NotFound from "../../../NotFound"
import { lang } from "../../../../constants/languages"
import { formatNumber } from "../../../../utility"

type SummaryTrackRoyaltiesProps = {
  summaryTrackRoyalties: SummaryTrackRoyalty[]
  handleSort: (field: string) => void
  sort: SortParams
}

const SummaryTrackRoyalties: FC<SummaryTrackRoyaltiesProps> = (props) => {
  const { summaryTrackRoyalties, handleSort, sort } = props

  const renderSummaryTrackRoyalties = () => {
    const sortedSummaryTrackRoyalties = summaryTrackRoyalties?.sort((a, b) => {
      const sortKeyA = sort.field === "sum_stream" ? "stream" : "totalAmountUsd"
      const sortKeyB = sort.field === "sum_stream" ? "streamChange" : "royaltyChange"

      const currentA = a[sortKeyA] || 0
      const changeA = a[sortKeyB] || 0
      const previousA = currentA - changeA
      const percentageA = previousA !== 0 ? (changeA / previousA) * 100 : 0

      const currentB = b[sortKeyA] || 0
      const changeB = b[sortKeyB] || 0
      const previousB = currentB - changeB
      const percentageB = previousB !== 0 ? (changeB / previousB) * 100 : 0

      if (sort.field && sort.direction) {
        if (sort.direction === "asc") {
          return currentA - currentB
        } else {
          return currentB - currentA
        }
      } else {
        return Math.abs(percentageB) - Math.abs(percentageA)
      }
    })

    return sortedSummaryTrackRoyalties?.map((summaryTrackRoyalty) => {
      const background = "#00000008"
      const isPositiveAmount = summaryTrackRoyalty.royaltyChange > 0
      const isPositiveStream = summaryTrackRoyalty.streamChange > 0
      return (
        <div
          className="flex p-2"
          style={{ width: "100%", background, borderRadius: "5px", borderBottom: "5px solid white" }}
        >
          <div style={{ width: "16%" }}>
            <div>
              <KTooltip
                position="bottom"
                content={<KSpan text={summaryTrackRoyalty?.user?.username || "-"} />}
                children={
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_FRONT_END_URL}/admin/user-asset?user_id=${summaryTrackRoyalty?.userId}`,
                        "_blank"
                      )
                    }
                  >
                    <KSpan
                      text={`${summaryTrackRoyalty?.user?.username?.substring(0, 14) || "-"}${
                        (summaryTrackRoyalty?.user?.username?.length || 0) > 14 ? "..." : ""
                      }`}
                    />
                  </div>
                }
              />
              <div
                className="cursor-pointer"
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_FRONT_END_URL}/admin/catalog?isrc=${summaryTrackRoyalty?.isrc}`,
                    "_blank"
                  )
                }
              >
                <KSpan text={summaryTrackRoyalty?.isrc || "-"} />
              </div>
            </div>
          </div>
          <div style={{ width: "30%" }}>
            <KSpan text={summaryTrackRoyalty.track?.title || "-"} />
          </div>
          <div style={{ width: "30%" }}>
            <KSpan text={summaryTrackRoyalty.track?.artistList?.map((artist) => artist.name)?.join(", ") || "-"} />
          </div>
          <div style={{ width: "12%" }}>
            <div className="flex flex-col gap-1">
              <KSpan text={formatNumber(summaryTrackRoyalty.stream || 0, 0)} />
              <div className="flex gap-1">
                <KSpan
                  text={`${isPositiveStream ? "+" : ""}${formatNumber(summaryTrackRoyalty.streamChange || 0, 1)}`}
                  color={isPositiveStream ? "#56E099" : "#FF5865"}
                />
                <KSpan
                  text={`(${(() => {
                    const current = summaryTrackRoyalty.stream || 0
                    const change = summaryTrackRoyalty.streamChange || 0
                    const previous = current - change
                    return previous !== 0 ? (change / previous) * 100 : 0
                  })().toFixed(0)}%)`}
                  color={isPositiveStream ? "#56E099" : "#FF5865"}
                />
              </div>
            </div>
          </div>
          <div style={{ width: "12%" }}>
            <div className="flex flex-col gap-1">
              <KSpan
                text={`${formatNumber(summaryTrackRoyalty.totalAmountUsd || 0, 1)} / ${formatNumber(
                  summaryTrackRoyalty.totalNetUsd || 0,
                  1
                )}`}
              />
              <div className="flex gap-1">
                <KSpan
                  text={`${isPositiveAmount ? "+" : ""}${formatNumber(summaryTrackRoyalty.royaltyChange || 0, 1)}`}
                  color={isPositiveAmount ? "#56E099" : "#FF5865"}
                />
                <KSpan
                  text={`(${(() => {
                    const current = summaryTrackRoyalty.totalAmountUsd || 0
                    const change = summaryTrackRoyalty.royaltyChange || 0
                    const previous = current - change
                    return previous !== 0 ? (change / previous) * 100 : 0
                  })().toFixed(0)}%)`}
                  color={isPositiveAmount ? "#56E099" : "#FF5865"}
                />
              </div>
            </div>
          </div>
        </div>
      )
    })
  }

  return (
    <div className="w-full h-full">
      <div
        className="py-5 px-7 min-h-[100%]"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div>
          <div className="flex mb-4" style={{ width: "100%" }}>
            <div style={{ width: "16%" }}>
              <KSpan color="#111" text={"User / Identifier"} />
            </div>
            <div style={{ width: "30%" }}>
              <KSpan color="#111" text={"Track Title"} />
            </div>
            <div style={{ width: "30%" }}>
              <KSpan color="#111" text={"Artist(s)"} />
            </div>
            <div
              className="cursor-pointer flex gap-1 items-center"
              style={{ width: "12%" }}
              onClick={() => handleSort("sum_stream")}
            >
              <KSpan color="#111" text={"Streams"} />
              {sort.field && sort.field === "sum_stream" && (
                <img
                  src={sort.direction === "asc" ? "/admin_icons/caret-up.svg" : "/admin_icons/caret-down.svg"}
                  alt="change-icon"
                  height={16}
                  width={16}
                />
              )}
            </div>
            <div
              className="cursor-pointer flex gap-1 items-center"
              style={{ width: "12%" }}
              onClick={() => handleSort("sum_amount_usd")}
            >
              <KSpan color="#111" text={"Gross / Net ($)"} />
              {sort.field && sort.field === "sum_amount_usd" && (
                <img
                  src={sort.direction === "asc" ? "/admin_icons/caret-up.svg" : "/admin_icons/caret-down.svg"}
                  alt="change-icon"
                  height={16}
                  width={16}
                />
              )}
            </div>
          </div>
          {summaryTrackRoyalties && !(summaryTrackRoyalties.length > 0) && (
            <div className="flex p-5 w-full justify-center mt-[100px]">
              <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
            </div>
          )}
          {summaryTrackRoyalties && summaryTrackRoyalties.length > 0 && (
            <div className="flex p-2 mb-2" style={{ width: "100%", background: "#00000008", borderRadius: "5px" }}>
              <div style={{ width: "76%" }}>
                <KSpan fontWeight={600} color="#111" text={"TOTAL"} />
              </div>
              <div style={{ width: "12%" }}>
                <div className="flex flex-col gap-1">
                  <KSpan
                    text={formatNumber(summaryTrackRoyalties.reduce((sum, item) => sum + (item.stream || 0), 0))}
                  />
                  <KSpan
                    text={formatNumber(summaryTrackRoyalties.reduce((sum, item) => sum + (item.streamChange || 0), 0))}
                  />
                </div>
              </div>
              <div style={{ width: "12%" }}>
                <div className="flex flex-col gap-1">
                  <KSpan
                    text={`${formatNumber(
                      summaryTrackRoyalties.reduce((sum, item) => sum + (item.totalAmountUsd || 0), 0),
                      1
                    )} / ${formatNumber(
                      summaryTrackRoyalties.reduce((sum, item) => sum + (item.totalNetUsd || 0), 0),
                      1
                    )}`}
                  />
                  <KSpan
                    text={formatNumber(
                      summaryTrackRoyalties.reduce((sum, item) => sum + (item.royaltyChange || 0), 0),
                      1
                    )}
                  />
                </div>
              </div>
            </div>
          )}
          {renderSummaryTrackRoyalties()}
        </div>
      </div>
    </div>
  )
}

export default SummaryTrackRoyalties
